import { Suspense } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'

import { PageLoader } from '@/components/common/PageLoader'
import { OloNotAvailable } from '@/components/OloNotAvailable'
import { V2NotEnabled } from '@/components/V2NotEnabled'
import { StoreClosed } from '@/components/StoreClosed'
import { NoActiveSchedule, SCHEDULE_UNAVAILABLE_CODES } from '@/components/NoActiveSchedule'
import { SystemHealthBanner } from '@/components/SystemHealthBanner/SystemHealthBanner'
import { useLocationBootstrap } from '@/hooks/useLocationBootstrap'
import { useFeatureFlag } from '@/providers/FeatureFlagProvider'
import { ApiError } from '@/lib/apiErrors'
import { useFormatMessage } from '@/translations/useFormatMessage'

function BootstrappedLocation() {
	const bootstrap = useLocationBootstrap()
	const systemHealthBannerEnabled = useFeatureFlag('ew-olo-system-health-banner', false)
	const v2Migration = useFeatureFlag('ew-olo-v2-migration', false)
	const v2Enabled = useFeatureFlag('ew-olo-v2-enabled', false)

	if (!bootstrap.isReady) return <PageLoader />
	if (bootstrap.ldIdentificationState === 'identifying') return <PageLoader />
	if (!v2Migration && !v2Enabled) return <V2NotEnabled locationName={bootstrap.location.name} />
	if (!bootstrap.location.oloStoreActive) return <OloNotAvailable />

	const systemHealth = bootstrap.location.systemHealth
	const showSystemHealthBanner =
		systemHealthBannerEnabled && (systemHealth === 'degraded' || systemHealth === 'down')

	return (
		<>
			{showSystemHealthBanner && systemHealth && (
				<SystemHealthBanner systemHealth={systemHealth} />
			)}
			<Outlet
				context={{
					location: bootstrap.location,
					isStoreClosureActive: bootstrap.isStoreClosureActive,
					activeStoreClosureMessage: bootstrap.activeStoreClosureMessage,
					hasSameDayClosure: bootstrap.hasSameDayClosure,
				}}
			/>
		</>
	)
}

// Renders StoreClosed for the POS_CONFIGURATION error path.
// Uses error.message — the backend message from errors[0].message — which is what
// the current STO application surfaces to the user for this error. No i18n key.
// Falls back to null so StoreClosed renders its own i18n fallback if absent.
function PosConfigurationFallback({ error }: { error: ApiError }) {
	const displayMessage = error.message?.trim() || null
	return <StoreClosed message={displayMessage} errorCode={error.code} />
}

// Renders StoreClosed for the LOCINA error path.
// Kept as a component so useFormatMessage is called inside the React tree
// where IntlProvider is already mounted — not at the ErrorBoundary level.
function LocationDisabledFallback() {
	const { t } = useFormatMessage()
	return <StoreClosed message={t('component:storeClosed:message:locationDisabled')} />
}

// Renders StoreClosed for the LONOFO (location not found) error path.
function LocationNotFoundFallback() {
	const { t } = useFormatMessage()
	return <StoreClosed message={t('component:storeClosed:message:locationNotFound')} />
}

export default function LocationPage() {
	const { ref } = useParams()
	return (
		<ErrorBoundary
			key={ref}
			renderError={(error) => {
				if (error instanceof ApiError && SCHEDULE_UNAVAILABLE_CODES.has(error.code)) {
					return <NoActiveSchedule />
				}
				// LODINC handled separately — not in SCHEDULE_UNAVAILABLE_CODES because that set
				// is also consumed by MenuLayout via toI18nKey, and LODINC is not a known olo:menu
				// code so it would map to 'generic' and absorb all unknown menu API errors.
				if (error instanceof ApiError && error.code === 'LODINC') {
					return <NoActiveSchedule />
				}
				if (error instanceof ApiError && error.code === 'LOCINA') {
					return <LocationDisabledFallback />
				}
				if (error instanceof ApiError && error.code === 'LONOFO') {
					return <LocationNotFoundFallback />
				}
				// POS_CONFIGURATION is returned by the backend when the location's POS
				// setup is incompatible with online ordering (e.g. exclusive taxes on UK
				// locations). error.message carries the user-facing text from errors[0].message —
				// V1 passes this through verbatim, no i18n key. Passing null falls back to
				// StoreClosed's own i18n fallback string.
				if (error instanceof ApiError && error.code === 'POS_CONFIGURATION') {
					return <PosConfigurationFallback error={error} />
				}
				return null
			}}
		>
			<Suspense fallback={<PageLoader />}>
				<BootstrappedLocation />
			</Suspense>
		</ErrorBoundary>
	)
}
